import { useState, useEffect, useRef } from "react";
import { api, useAuth } from "@/App";
import { 
  UserCircle, Upload, Download, Plus, Trash2, Search, 
  Phone, Mail, MapPin, FileSpreadsheet, CheckCircle, AlertCircle, Edit, Wand2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function ClientsView() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cleanDialog, setCleanDialog] = useState(false);
  const [health, setHealth] = useState(null);
  const [dupGroups, setDupGroups] = useState([]);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [mergingKey, setMergingKey] = useState(null);

  const loadDuplicates = async () => {
    setLoadingHealth(true);
    try {
      const [h, d] = await Promise.all([
        api.get("/maintenance/data-health"),
        api.get("/maintenance/duplicates"),
      ]);
      setHealth(h.data);
      const groups = [...(d.data.phone_groups || []), ...(d.data.name_groups || [])]
        .map((g, i) => {
          const keepId = g.clients[0]?.id;
          const mergeSel = {};
          g.clients.forEach((c) => { if (c.id !== keepId) mergeSel[c.id] = true; });
          return { ...g, gid: `${g.type}-${i}`, keepId, mergeSel };
        });
      setDupGroups(groups);
    } catch { toast.error("Erreur lors de l'analyse"); }
    setLoadingHealth(false);
  };

  const openClean = () => { setCleanDialog(true); setHealth(null); setDupGroups([]); loadDuplicates(); };

  const runNormalize = async () => {
    setNormalizing(true);
    try {
      const { data: d } = await api.post("/maintenance/normalize");
      toast.success(`Numéros nettoyés : ${d.phones_normalized} · véhicules dédupliqués : ${d.vehicules_dedup}`);
      await loadDuplicates();
      fetchClients();
    } catch { toast.error("Erreur lors de la normalisation"); }
    setNormalizing(false);
  };

  const setKeep = (gid, keepId) => setDupGroups((gs) => gs.map((g) => {
    if (g.gid !== gid) return g;
    const mergeSel = {};
    g.clients.forEach((c) => { if (c.id !== keepId) mergeSel[c.id] = g.mergeSel[c.id] !== false; });
    return { ...g, keepId, mergeSel };
  }));

  const toggleMerge = (gid, id) => setDupGroups((gs) => gs.map((g) =>
    g.gid === gid ? { ...g, mergeSel: { ...g.mergeSel, [id]: !g.mergeSel[id] } } : g));

  const mergeGroup = async (g) => {
    const merge_ids = g.clients.map((c) => c.id).filter((id) => id !== g.keepId && g.mergeSel[id]);
    if (merge_ids.length === 0) { toast.info("Sélectionnez au moins une fiche à fusionner"); return; }
    setMergingKey(g.gid);
    try {
      const { data: d } = await api.post("/maintenance/merge", { keep_id: g.keepId, merge_ids });
      toast.success(d.message || "Fusionné");
      setDupGroups((gs) => gs.filter((x) => x.gid !== g.gid));
      fetchClients();
    } catch { toast.error("Erreur lors de la fusion"); }
    setMergingKey(null);
  };

  const ignoreGroup = (gid) => setDupGroups((gs) => gs.filter((g) => g.gid !== gid));
  
  const [newClient, setNewClient] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    adresse: ""
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const handleAddClient = async () => {
    if (!newClient.nom || !newClient.telephone) {
      toast.error("Nom et téléphone obligatoires");
      return;
    }
    setSaving(true);
    try {
      if (editClient) {
        await api.put(`/clients/${editClient.id}`, newClient);
        toast.success("Client modifié");
      } else {
        await api.post("/clients", newClient);
        toast.success("Client ajouté");
      }
      setDialogOpen(false);
      setEditClient(null);
      setNewClient({ nom: "", prenom: "", telephone: "", email: "", adresse: "" });
      fetchClients();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleEditClick = (client) => {
    setEditClient(client);
    setNewClient({
      nom: client.nom,
      prenom: client.prenom || "",
      telephone: client.telephone,
      email: client.email || "",
      adresse: client.adresse || ""
    });
    setDialogOpen(true);
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Client supprimé");
      setDeleteDialog(null);
      fetchClients();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await api.post("/clients/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${response.data.imported} clients importés`);
      setImportDialog(false);
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur lors de l'import");
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExportClients = async () => {
    try {
      const response = await api.get("/clients/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "clients.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const filteredClients = clients.filter(c => {
    const search = searchTerm.toLowerCase();
    return (
      c.nom.toLowerCase().includes(search) ||
      (c.prenom && c.prenom.toLowerCase().includes(search)) ||
      c.telephone.includes(search) ||
      (c.email && c.email.toLowerCase().includes(search))
    );
  });

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="clients-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <UserCircle className="h-8 w-8" />
            Clients
          </h1>
          <p className="text-slate-600 mt-1">{clients.length} client(s) enregistré(s)</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openClean} data-testid="clean-duplicates-btn">
              <Wand2 className="h-4 w-4 mr-2" />
              Nettoyer les doublons
            </Button>
          )}
          <Button variant="outline" onClick={() => setImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Importer
          </Button>
          <Button variant="outline" onClick={handleExportClients}>
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
          <Button onClick={() => { setEditClient(null); setNewClient({ nom: "", prenom: "", telephone: "", email: "", adresse: "" }); setDialogOpen(true); }} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher (nom, prénom, téléphone, email...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <UserCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun client trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nom</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Adresse</TableHead>
                  <TableHead>Véhicules</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} className="hover:bg-slate-50">
                    <TableCell>
                      <p className="font-semibold">{client.prenom} {client.nom}</p>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${client.telephone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Phone className="h-3 w-3" />
                        {client.telephone}
                      </a>
                    </TableCell>
                    <TableCell>
                      {client.email ? (
                        <a href={`mailto:${client.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                          <Mail className="h-3 w-3" />
                          {client.email}
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">
                      {client.adresse || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{client.vehicles_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditClick(client)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setDeleteDialog(client)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editClient ? "Modifier le client" : "Nouveau client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={newClient.prenom}
                  onChange={(e) => setNewClient({ ...newClient, prenom: e.target.value })}
                  placeholder="Jean"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newClient.nom}
                  onChange={(e) => setNewClient({ ...newClient, nom: e.target.value })}
                  placeholder="Dupont"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Téléphone *</Label>
              <Input
                value={newClient.telephone}
                onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })}
                placeholder="06 XX XX XX XX"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="jean@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={newClient.adresse}
                onChange={(e) => setNewClient({ ...newClient, adresse: e.target.value })}
                placeholder="Adresse complète"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddClient} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : (editClient ? "Modifier" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Importer des clients
            </DialogTitle>
            <DialogDescription>
              Importez vos clients depuis un fichier Excel (.xlsx, .xls) ou ODS
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-sm text-slate-600 mb-4">
                Colonnes attendues : <strong>Nom, Prénom, Téléphone, Email, Adresse</strong>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.ods,.csv"
                onChange={handleImportFile}
                className="hidden"
                id="file-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? (
                  <>
                    <div className="spinner mr-2" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Choisir un fichier
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog?.prenom} {deleteDialog?.nom} sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteClient(deleteDialog?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nettoyer les doublons */}
      <Dialog open={cleanDialog} onOpenChange={setCleanDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="clean-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5" />Nettoyer les doublons</DialogTitle>
            <DialogDescription>
              Vous décidez de chaque fusion : un particulier et sa société peuvent partager un numéro. Rien n'est fusionné sans votre validation.
            </DialogDescription>
          </DialogHeader>

          {/* Étape 1 : normalisation sûre */}
          <div className="rounded-lg border p-3 flex items-center justify-between gap-3 bg-slate-50">
            <div className="text-sm">
              <p className="font-medium text-slate-700">Normaliser les numéros & véhicules</p>
              <p className="text-slate-500 text-xs">
                {health ? `${health.phones_to_normalize} numéro(s) à nettoyer · ${health.duplicate_vehicules} véhicule(s) en double` : "…"}
              </p>
            </div>
            <Button variant="outline" onClick={runNormalize} disabled={normalizing} data-testid="run-normalize-btn">
              {normalizing ? <div className="spinner" /> : "Normaliser"}
            </Button>
          </div>

          {/* Étape 2 : doublons clients à valider */}
          <div className="space-y-3 mt-2">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-1">
              Doublons clients à vérifier {dupGroups.length > 0 && <Badge className="bg-red-600 ml-1">{dupGroups.length}</Badge>}
            </div>
            {loadingHealth ? (
              <div className="flex justify-center py-6"><div className="spinner" /></div>
            ) : dupGroups.length === 0 ? (
              <p className="text-emerald-600 text-sm flex items-center gap-1"><CheckCircle className="h-4 w-4" />Aucun doublon client à valider</p>
            ) : (
              dupGroups.map((g) => (
                <div key={g.gid} className="rounded-lg border p-3 space-y-2" data-testid="dup-group">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Badge variant="outline">{g.type === "phone" ? "Même téléphone" : "Même nom"}</Badge>
                    <span className="font-mono">{g.key}</span>
                  </div>
                  <div className="space-y-1">
                    {g.clients.map((c) => (
                      <div key={c.id} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${g.keepId === c.id ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"}`}>
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input type="radio" name={`keep-${g.gid}`} checked={g.keepId === c.id} onChange={() => setKeep(g.gid, c.id)} data-testid="keep-radio" />
                          <span className="flex-1">
                            <span className="font-medium">{c.nom} {c.prenom || ""}</span>
                            <span className="text-slate-400"> · {c.telephone || "pas de tél."}{c.email ? ` · ${c.email}` : ""} · {c.vehicles_count} véh.</span>
                          </span>
                        </label>
                        {g.keepId === c.id ? (
                          <Badge className="bg-emerald-600 text-[10px]">À conserver</Badge>
                        ) : (
                          <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer whitespace-nowrap">
                            <input type="checkbox" checked={!!g.mergeSel[c.id]} onChange={() => toggleMerge(g.gid, c.id)} data-testid="merge-checkbox" />
                            fusionner
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={() => ignoreGroup(g.gid)} data-testid="ignore-group-btn">Garder séparés</Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={mergingKey === g.gid} onClick={() => mergeGroup(g)} data-testid="merge-group-btn">
                      {mergingKey === g.gid ? <div className="spinner" /> : "Fusionner la sélection"}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
